import { useState, useEffect } from "react";
import React from "react";
import { ArrowLeft, Droplets, Thermometer, Gauge, Wifi, AlertTriangle, CheckCircle, MapPin, Calendar, Clock } from "lucide-react";
import { Link } from "react-router-dom";
import { getVistas } from "../api/posts";

export default function CaleraDemo() {
    const [selectedCistern, setSelectedCistern] = useState(1011);
    const [cisterns, setCisterns] = useState([]);
    const [loading, setLoading] = useState(true);

    // Mapeo de nombres de cisternas por topiccuenta
    const cisternNames = {
        1011: "Cisterna Sur",
        1017: "Cisterna Este", 
        1018: "Cisterna Norte",
        1019: "Cisterna Oeste"
    };

    // Mapeo de sectores por topiccuenta
    const cisternSectors = {
        1011: "Sector A",
        1017: "Sector B",
        1018: "Sector C", 
        1019: "Sector D"
    };

    const generateChartData = async () => {
        try {
            const vistas = await getVistas();
            console.log('Datos de la API:', vistas.data);
            
            // Filtrar solo los topics que necesitamos
            const targetTopics = [1011, 1017, 1018, 1019];
            const filteredData = vistas.data.filter(item => 
                targetTopics.includes(parseInt(item.topiccuenta))
            );

            // Eliminar duplicados manteniendo el registro más reciente de cada topiccuenta
            const uniqueData = {};
            filteredData.forEach(item => {
                const topiccuenta = parseInt(item.topiccuenta);
                const currentTime = new Date(item.updatedAt);
                
                // Si no existe o el actual es más reciente, reemplazar
                if (!uniqueData[topiccuenta] || 
                    new Date(uniqueData[topiccuenta].updatedAt) < currentTime) {
                    uniqueData[topiccuenta] = item;
                }
            });

            // Convertir el objeto de vuelta a array y mapear los datos
            const mappedCisterns = Object.values(uniqueData).map(item => {
                const topiccuenta = parseInt(item.topiccuenta);
                
                // Calcular el tiempo desde la última actualización
                const lastUpdateTime = new Date(item.updatedAt);
                const now = new Date();
                const diffMinutes = Math.floor((now - lastUpdateTime) / (1000 * 60));
                const lastUpdate = diffMinutes < 1 ? "Ahora" : 
                                 diffMinutes === 1 ? "1 min" : 
                                 diffMinutes < 60 ? `${diffMinutes} min` : 
                                 `${Math.floor(diffMinutes / 60)}h`;

                return {
                    id: topiccuenta,
                    name: cisternNames[topiccuenta] || `Cisterna ${topiccuenta}`,
                    level: item.dist || 0, // dist → level
                    temp: item.temp || 0,  // temp → temp
                    status: item.estado || "unknown", // estado → status
                    location: cisternSectors[topiccuenta] || `Sector ${topiccuenta}`,
                    lastUpdate: lastUpdate,
                    topiccuenta: topiccuenta
                };
            }).sort((a, b) => a.id - b.id); // Ordenar por ID para consistencia

            setCisterns(mappedCisterns);
            
            // Si no hay cisterna seleccionada o la seleccionada no existe, seleccionar la primera
            if (!selectedCistern || !mappedCisterns.find(c => c.id === selectedCistern)) {
                if (mappedCisterns.length > 0) {
                    setSelectedCistern(mappedCisterns[0].id);
                }
            }
            
            setLoading(false);
        } catch (error) {
            console.error('Error al obtener datos:', error);
            setLoading(false);
        }
    };

    useEffect(() => {
        generateChartData();
        const fetchInterval = setInterval(() => {
            generateChartData();
        }, 10000);
        return () => clearInterval(fetchInterval);
    }, []);

    const getStatusColor = (status) => {
        switch (status?.toLowerCase()) {
            case 'normal':
            case 'ok':
            case 'bueno':
            case 'activo':
                return 'text-green-600 bg-green-50 border-green-200';
            case 'warning':
            case 'alerta':
            case 'medio':
                return 'text-yellow-600 bg-yellow-50 border-yellow-200';
            case 'critical':
            case 'critico':
            case 'bajo':
            case 'error':
                return 'text-red-600 bg-red-50 border-red-200';
            default: 
                return 'text-gray-600 bg-gray-50 border-gray-200';
        }
    };

    const getStatusIcon = (status) => {
        switch (status?.toLowerCase()) {
            case 'normal':
            case 'ok':
            case 'bueno':
            case 'activo':
                return <CheckCircle className="w-4 h-4" />;
            case 'warning':
            case 'alerta':
            case 'medio':
            case 'critical':
            case 'critico':
            case 'bajo':
            case 'error':
                return <AlertTriangle className="w-4 h-4" />;
            default: 
                return <CheckCircle className="w-4 h-4" />;
        }
    };

    // Calcular estadísticas en tiempo real
    const getStats = () => {
        if (cisterns.length === 0) {
            return {
                totalCisterns: 0,
                averageLevel: 0,
                activeAlerts: 0,
                connectivity: 0
            };
        }

        const totalCisterns = cisterns.length;
        const averageLevel = Math.round(cisterns.reduce((sum, c) => sum + c.level, 0) / totalCisterns);
        const activeAlerts = cisterns.filter(c => 
            c.status?.toLowerCase() === 'warning' || 
            c.status?.toLowerCase() === 'critical' ||
            c.status?.toLowerCase() === 'alerta' ||
            c.status?.toLowerCase() === 'critico' ||
            c.status?.toLowerCase() === 'bajo' ||
            c.status?.toLowerCase() === 'error'
        ).length;
        
        // Calcular conectividad basada en cuántas cisternas han reportado recientemente
        const recentUpdates = cisterns.filter(c => {
            const lastUpdate = c.lastUpdate;
            return lastUpdate === "Ahora" || lastUpdate.includes("min") || lastUpdate === "1h";
        }).length;
        const connectivity = Math.round((recentUpdates / totalCisterns) * 100);

        return { totalCisterns, averageLevel, activeAlerts, connectivity };
    };

    const stats = getStats();

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
            {/* Header */}
            <header className="sticky top-0 z-50 w-full border-b bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/60">
                <div className="w-full flex h-20 items-center justify-between px-6">
                    <div className="flex items-center gap-4">
                        <Link to="/" className="flex items-center gap-2 text-blue-600 hover:text-blue-800 transition-colors">
                            <ArrowLeft className="w-5 h-5" />
                            <span className="font-medium">Volver</span>
                        </Link>
                        <div className="h-6 w-px bg-gray-300"></div>
                        <div className="flex items-center gap-2">
                            <Droplets className="w-6 h-6 text-blue-600" />
                            <span className="text-xl font-semibold text-gray-900">Sistema Hídrico La Calera</span>
                        </div>
                    </div>
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-green-50 text-green-700 rounded-full text-sm font-medium">
                        <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                        <span>Sistema Activo</span>
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <main className="relative overflow-hidden">
                {/* Background Effects */}
                <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>
                <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-200 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse"></div>
                <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-indigo-200 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse animation-delay-2000"></div>

                <div className="relative container mx-auto px-6 py-12 max-w-7xl">
                    {/* Hero Section */}
                    <div className="text-center mb-12">
                        <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-blue-100 to-indigo-100 text-blue-800 rounded-full text-sm font-medium mb-6">
                            <MapPin className="w-4 h-4 mr-2" />
                            Córdoba, Argentina
                        </div>
                        <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-gray-900 mb-6">
                            Demo <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">Interactivo</span>
                        </h1>
                        <p className="max-w-3xl mx-auto text-xl text-gray-600 leading-relaxed mb-8">
                            Monitoreo en tiempo real de 33 cisternas distribuidas en el municipio montañoso de La Calera.
                            Sistema inteligente que previene cortes de agua y garantiza abastecimiento continuo.
                        </p>
                    </div>

                    {/* Stats Overview */}
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-12">
                        <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-white/20 shadow-lg">
                            <div className="flex items-center justify-between mb-4">
                                <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center">
                                    <Droplets className="w-6 h-6 text-white" />
                                </div>
                                <span className="text-2xl font-bold text-gray-900">
                                    {loading ? "..." : stats.totalCisterns}
                                </span>
                            </div>
                            <h3 className="text-sm font-medium text-gray-600 mb-1">Cisternas Monitoreadas</h3>
                            <p className="text-xs text-green-600">
                                {loading ? "Cargando..." : `${stats.totalCisterns} ejemplos de 33 activas`}
                            </p>
                        </div>

                        <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-white/20 shadow-lg">
                            <div className="flex items-center justify-between mb-4">
                                <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-emerald-600 rounded-xl flex items-center justify-center">
                                    <Gauge className="w-6 h-6 text-white" />
                                </div>
                                <span className="text-2xl font-bold text-gray-900">
                                    {loading ? "..." : `${stats.averageLevel}%`}
                                </span>
                            </div>
                            <h3 className="text-sm font-medium text-gray-600 mb-1">Nivel Promedio</h3>
                            <p className={`text-xs ${
                                loading ? "text-gray-400" :
                                stats.averageLevel > 70 ? "text-green-600" :
                                stats.averageLevel > 30 ? "text-yellow-600" : "text-red-600"
                            }`}>
                                {loading ? "Cargando..." :
                                 stats.averageLevel > 70 ? "Óptimo" :
                                 stats.averageLevel > 30 ? "Medio" : "Bajo"}
                            </p>
                        </div>

                        <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-white/20 shadow-lg">
                            <div className="flex items-center justify-between mb-4">
                                <div className="w-12 h-12 bg-gradient-to-r from-orange-500 to-red-600 rounded-xl flex items-center justify-center">
                                    <AlertTriangle className="w-6 h-6 text-white" />
                                </div>
                                <span className="text-2xl font-bold text-gray-900">
                                    {loading ? "..." : stats.activeAlerts}
                                </span>
                            </div>
                            <h3 className="text-sm font-medium text-gray-600 mb-1">Alertas Activas</h3>
                            <p className={`text-xs ${
                                loading ? "text-gray-400" :
                                stats.activeAlerts === 0 ? "text-green-600" : "text-orange-600"
                            }`}>
                                {loading ? "Cargando..." :
                                 stats.activeAlerts === 0 ? "Todo normal" : "Requieren atención"}
                            </p>
                        </div>

                        <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-white/20 shadow-lg">
                            <div className="flex items-center justify-between mb-4">
                                <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-violet-600 rounded-xl flex items-center justify-center">
                                    <Wifi className="w-6 h-6 text-white" />
                                </div>
                                <span className="text-2xl font-bold text-gray-900">
                                    {loading ? "..." : `${stats.connectivity}%`}
                                </span>
                            </div>
                            <h3 className="text-sm font-medium text-gray-600 mb-1">Conectividad</h3>
                            <p className={`text-xs ${
                                loading ? "text-gray-400" :
                                stats.connectivity > 80 ? "text-green-600" :
                                stats.connectivity > 50 ? "text-yellow-600" : "text-red-600"
                            }`}>
                                {loading ? "Cargando..." :
                                 stats.connectivity > 80 ? "Excelente" :
                                 stats.connectivity > 50 ? "Buena" : "Limitada"}
                            </p>
                        </div>
                    </div>

                    {/* Main Dashboard */}
                    <div className="grid lg:grid-cols-3 gap-8">
                        {/* Cisterns List */}
                        <div className="lg:col-span-1">
                            <div className="bg-white/80 backdrop-blur-sm rounded-2xl border border-white/20 shadow-lg overflow-hidden">
                                <div className="p-6 border-b border-gray-100">
                                    <h2 className="text-xl font-bold text-gray-900 flex items-center">
                                        <div className="w-3 h-3 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-full mr-3"></div>
                                        Cisternas Monitoreadas
                                    </h2>
                                </div>
                                <div className="p-4 space-y-3 max-h-96 overflow-y-auto">
                                    {cisterns.map((cistern) => (
                                        <div
                                            key={cistern.id}
                                            onClick={() => setSelectedCistern(cistern.id)}
                                            className={`p-4 rounded-xl border cursor-pointer transition-all duration-300 hover:shadow-md ${
                                                selectedCistern === cistern.id
                                                    ? 'bg-blue-50 border-blue-200 shadow-md'
                                                    : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                                            }`}
                                        >
                                            <div className="flex items-center justify-between mb-2">
                                                <h3 className="font-semibold text-gray-900">{cistern.name}</h3>
                                                <div className={`flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(cistern.status)}`}>
                                                    {getStatusIcon(cistern.status)}
                                                    <span className="ml-1 capitalize">{cistern.status}</span>
                                                </div>
                                            </div>
                                            <div className="flex items-center justify-between text-sm text-gray-600">
                                                <span>{cistern.location}</span>
                                                <span className="flex items-center">
                                                    <Clock className="w-3 h-3 mr-1" />
                                                    {cistern.lastUpdate}
                                                </span>
                                            </div>
                                            <div className="mt-3">
                                                <div className="flex justify-between text-xs text-gray-500 mb-1">
                                                    <span>Nivel</span>
                                                    <span>{cistern.level}%</span>
                                                </div>
                                                <div className="w-full bg-gray-200 rounded-full h-2">
                                                    <div
                                                        className={`h-2 rounded-full transition-all duration-300 ${
                                                            cistern.level > 70 ? 'bg-green-500' :
                                                            cistern.level > 30 ? 'bg-yellow-500' : 'bg-red-500'
                                                        }`}
                                                        style={{ width: `${cistern.level}%` }}
                                                    ></div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Selected Cistern Details */}
                        <div className="lg:col-span-2">
                            {cisterns.filter(c => c.id === selectedCistern).map(cistern => (
                                <div key={cistern.id} className="space-y-6">
                                    {/* Cistern Header */}
                                    <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 border border-white/20 shadow-lg">
                                        <div className="flex items-center justify-between mb-6">
                                            <div>
                                                <h2 className="text-2xl font-bold text-gray-900 mb-2">{cistern.name}</h2>
                                                <p className="text-gray-600 flex items-center">
                                                    <MapPin className="w-4 h-4 mr-2" />
                                                    {cistern.location} - La Calera, Córdoba
                                                </p>
                                            </div>
                                            <div className={`flex items-center px-4 py-2 rounded-full text-sm font-medium border ${getStatusColor(cistern.status)}`}>
                                                {getStatusIcon(cistern.status)}
                                                <span className="ml-2 capitalize">{cistern.status}</span>
                                            </div>
                                        </div>

                                        {/* Metrics */}
                                        <div className="grid grid-cols-2 gap-6">
                                            <div className="text-center p-6 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-100">
                                                <Droplets className="w-8 h-8 text-blue-600 mx-auto mb-3" />
                                                <div className="text-3xl font-bold text-gray-900 mb-1">{cistern.level}%</div>
                                                <div className="text-sm text-gray-600">Nivel de Agua</div>
                                                <div className="mt-3 w-full bg-blue-200 rounded-full h-3">
                                                    <div
                                                        className="h-3 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full transition-all duration-500"
                                                        style={{ width: `${cistern.level}%` }}
                                                    ></div>
                                                </div>
                                            </div>

                                            <div className="text-center p-6 bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl border border-green-100">
                                                <Thermometer className="w-8 h-8 text-green-600 mx-auto mb-3" />
                                                <div className="text-3xl font-bold text-gray-900 mb-1">{cistern.temp}°C</div>
                                                <div className="text-sm text-gray-600">Temperatura</div>
                                                <div className="mt-3 text-xs text-green-600 font-medium">Rango Normal</div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Additional Info */}
                                    <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-white/20 shadow-lg">
                                        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                                            <Calendar className="w-5 h-5 mr-2 text-blue-600" />
                                            Información del Sistema
                                        </h3>
                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                            <div className="p-4 bg-gray-50 rounded-xl">
                                                <div className="text-sm text-gray-600 mb-1">Última Actualización</div>
                                                <div className="font-semibold text-gray-900">Hace {cistern.lastUpdate}</div>
                                            </div>
                                            <div className="p-4 bg-gray-50 rounded-xl">
                                                <div className="text-sm text-gray-600 mb-1">Capacidad</div>
                                                <div className="font-semibold text-gray-900">50,000 L</div>
                                            </div>
                                            <div className="p-4 bg-gray-50 rounded-xl">
                                                <div className="text-sm text-gray-600 mb-1">Tipo de Sensor</div>
                                                <div className="font-semibold text-gray-900">Presion Hidrostática</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Bottom CTA */}
                    <div className="text-center mt-16">
                        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-3xl p-8 text-white">
                            <h3 className="text-2xl font-bold mb-4">¿Interesado en implementar un sistema similar?</h3>
                            <p className="text-blue-100 mb-6 max-w-2xl mx-auto">
                                Nuestro equipo puede diseñar y implementar una solución IoT personalizada para tu municipio o empresa.
                            </p>
                            <Link to="/#contact">
                                <div className="inline-flex items-center px-8 py-4 bg-white text-blue-600 font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
                                    <span className="mr-2">Contactar Ahora</span>
                                    <ArrowLeft className="w-5 h-5 rotate-180" />
                                </div>
                            </Link>
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
}